import { Link } from "react-router-dom";
import { BotonPlay } from "./Reproductor";
import Caratula from "./Caratula";
import { fraccionVida, textoCaduca, mesAnio, minutos } from "./feed";
import { IDIOMA, de, rutaOriginal, tr, seccion } from "../i18n/idioma";

/* Piezas compartidas por portada y landings. Una sola forma de pintar cada
   tipología en todo el sitio: así el origen (auto / dirigido / podcast) se lee
   igual en cualquier página. */

export const rutaNoticia = (n) => `/noticias/${n.slug}`;
export const rutaEstudio = (e) => `/estudios/${e.slug}`;
export const rutaEpisodio = (p) => `/podcast/${p.slug}`;

export function pistaDe(item, tipo) {
  // En inglés, el resumen hablado inglés si existe; si no, el español.
  const a = IDIOMA === "en" && item?.en?.audio ? item.en.audio : item?.audio;
  const audio = a?.url || a;
  if (!audio) return null;
  return { id: `${tipo}:${item.id || item.slug}`, titulo: de(item, "titulo"), tipo, audio };
}

export function Vida({ caduca }) {
  return (
    <>
      <div className="y-vida" aria-hidden="true">
        <i style={{ width: `${fraccionVida(caduca) * 100}%` }} />
      </div>
      <div className="y-meta" style={{ marginTop: 8 }}>{textoCaduca(caduca)}</div>
    </>
  );
}

// Rótulos por idioma: funciones, no constantes, porque el idioma se fija tras evaluar los módulos.
export const plantilla = (k) => ({
  cronica: tr("Crónica", "Report"), breve: tr("Breve · última hora", "Brief · breaking"),
  datos: tr("El dato", "The figure"), contraste: tr("Contraste", "Fact check"),
}[k] || k);

// Una noticia en /en sin bloque `en` sale en español, con su marca.
export const sinTraducir = (n) => IDIOMA === "en" && !n?.en;
export function MarcaIdioma({ n }) {
  return sinTraducir(n) ? <span className="y-badge" lang="en">In Spanish</span> : null;
}
// Bajo el titular de una pieza traducida: rótulo y enlace al original (la misma ruta sin /en).
export function Traducida({ n, ruta }) {
  if (IDIOMA !== "en" || !n?.en) return null;
  return (
    <p className="y-meta" style={{ margin: "6px 0 0" }}>
      English edition · <a className="y-link" href={rutaOriginal(ruta)} lang="es">Read in Spanish</a>
    </p>
  );
}

export function MetaNoticia({ n }) {
  const partes = [seccion(n.seccion), n.plantilla && n.plantilla !== "cronica" ? plantilla(n.plantilla).split(" ·")[0] : null,
    n.fuentes?.length ? tr(`${n.fuentes.length} fuentes`, n.fuentes.length === 1 ? "1 source" : `${n.fuentes.length} sources`) : null,
    textoCaduca(n.caduca)];
  return <span className="y-meta">{partes.filter(Boolean).join(" · ")}</span>;
}

export function Confianza({ valor }) {
  if (!valor) return null;
  const txt = { corroborada: tr("Corroborada", "Corroborated"), no_confirmada: tr("No confirmada", "Unconfirmed"),
    en_desarrollo: tr("En desarrollo", "Developing") }[valor] || valor;
  return <span className={`y-confianza y-confianza--${valor}`}>{txt}</span>;
}

// En la portada van tres en fila: la barra de vida se ancla al pie (margin-top:auto) para que
// las tres queden alineadas aunque los títulos tengan distinto número de líneas.
export function NoticiaColumna({ n }) {
  return (
    <article style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <p className="y-eyebrow">{seccion(n.seccion)} <MarcaIdioma n={n} /></p>
      <h3 style={{ fontSize: 19, fontWeight: 700, lineHeight: 1.22, letterSpacing: "-.01em", margin: "8px 0 0" }}>
        <Link className="y-link" to={rutaNoticia(n)}>{de(n, "titulo")}</Link>
      </h3>
      <div style={{ marginTop: "auto", paddingTop: 4 }}><Vida caduca={n.caduca} /></div>
    </article>
  );
}

export function TarjetaEstudio({ e, rotulo = tr("Estudio automático", "Automated study") }) {
  return (
    <Link to={rutaEstudio(e)} className="y-card y-card--auto">
      <p className="y-eyebrow">{rotulo}</p>
      <h3 className="y-card-t">{de(e, "titulo")}</h3>
      <p className="y-card-p" style={{ color: "var(--y-auto-ink)" }}>
        {tr(`Generado a partir de ${e.noticias?.length || 0} noticias${e.seccion ? ` sobre ${e.seccion.toLowerCase()}` : ""}.`,
          `Built from ${e.noticias?.length || 0} news stories${e.seccion ? ` on ${seccion(e.seccion).toLowerCase()}` : ""}.`)}
      </p>
      <p style={{ margin: "14px 0 0", fontWeight: 600, fontSize: 14, color: "var(--y-auto-head)" }}>{tr("Abrir estudio →", "Open study →")}</p>
    </Link>
  );
}

export function TarjetaExpediente({ t, compacta = false }) {
  return (
    <Link to={t.path} className="y-card" style={compacta ? { padding: "18px 20px" } : undefined}>
      <span className="y-badge y-badge--dir">{etiquetaCorta(de(t, "etiqueta"))}</span>
      <h3 className="y-card-t" style={{ fontSize: compacta ? 17 : 19, fontWeight: 700 }}>{de(t, "titulo")}</h3>
      {!compacta && t.subtitulo && <p className="y-card-p">{recorta(de(t, "subtitulo"), 140)}</p>}
      {t.publicado && <p className="y-meta" style={{ margin: "12px 0 0" }}>{mesAnio(t.publicado)}</p>}
    </Link>
  );
}

// Tipo de episodio (dato en español en el feed) y su rótulo en la página.
const TIPO_EN = { "Automático": "Automated", "Elaborado": "Produced" };
export const tipoPodcast = (p) => tr(p.tipo, TIPO_EN[p.tipo] || p.tipo);

export function BloquePodcast({ p, rotulo = "Podcast" }) {
  if (!p) return null;
  const otro = (p.idioma || "es") !== IDIOMA;
  return (
    <div className="y-card y-card--pod" style={{ borderRadius: 28 }}>
      <Link to={rutaEpisodio(p)} tabIndex={-1} aria-hidden="true"><Caratula p={p} forma="ancha" style={{ marginBottom: 16 }} /></Link>
      <p className="y-eyebrow">{rotulo} · {tipoPodcast(p)}{otro && ` · ${tr("En inglés", "In Spanish")}`}</p>
      <h3 className="y-card-t" style={{ color: "var(--y-pod-ink)", fontSize: 21 }}>
        <Link className="y-link" to={rutaEpisodio(p)}>{de(p, "titulo")}</Link>
      </h3>
      <div style={{ display: "flex", alignItems: "center", gap: 14, marginTop: 14 }}>
        <BotonPlay pista={pistaDe(p, `Podcast · ${tipoPodcast(p)}`)} />
        <span className="y-meta" style={{ color: "var(--y-pod-sec)" }}>{minutos(p.duracion)}</span>
      </div>
    </div>
  );
}

export function Vacio({ children }) {
  return <div className="y-vacio">{children}</div>;
}

export function etiquetaCorta(etq = "") {
  return String(etq).split("·")[0].trim();
}

export function recorta(s = "", n = 160) {
  return s.length > n ? s.slice(0, s.lastIndexOf(" ", n)) + "…" : s;
}
